import {defineStore} from "pinia";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import type {AutoCompleteResult} from "@/app/shared/components/aeg-autocomplete/aeg-autocomplete.ts";
import {readonly, ref} from "vue";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import type {BillingAddress} from "@/app/shared/components/aeg-address-form/aeg-address-form.ts";
import {isEmpty} from "lodash-es";

const ADDRESS_SEPARATOR = ', ';

export const useLocationStore = defineStore('location-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const loadingState = new LoadingState();

    const streetData = ref<readonly AutoCompleteResult[]>([]);
    const streetDataFetching = ref<boolean>(false);
    
    const houseNumberData = ref<readonly AutoCompleteResult[]>([]);
    const houseNumberDataFetching = ref<boolean>(false);
    
    const fetchStreetSuggestions = async (street: string): Promise<void> => {
        streetDataFetching.value = true;
        try {
            const response = await apiProxy.location_GetAddressSuggestion(street);
            streetData.value = response.map((item): AutoCompleteResult => {
                const parts = item.split(',');
                return {
                    title: parts[0],
                    subtitle: parts[1],
                    value: item,
                };
            });
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
        } finally {
            streetDataFetching.value = false;
        }
    };

    const fetchHouseNumberSuggestions = async (street: string, city: string, houseNumber: string): Promise<void> => {
        try {
            if (!street || !city) {
                houseNumberData.value = [];
                return;
            }
            
            houseNumberDataFetching.value = true;
            const response = await apiProxy.location_GetStreetNumbers(street, city);
            if (isEmpty(response)) {
                return;
            }
            houseNumberData.value = response
                .filter((currentNr: string) => {
                    if (isEmpty(currentNr)) {
                        return false;
                    }
                    if (isEmpty(houseNumber)) {
                        return true;
                    }
                    return currentNr.startsWith(houseNumber);
                }).map((currentNr: string): AutoCompleteResult => {
                    return {
                        title: currentNr,
                        value: currentNr,
                    };
                });
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
        } finally {
            houseNumberDataFetching.value = false;
        }
    };
    
    const fetchPostalCodes = async (street: string, city: string): Promise<string | undefined> => {
        try {
            const response = await apiProxy.location_GetZipCodes(street, city);
            return response && response.length > 0 ? response[0] : '';
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
        }
    };
    
    const parseBillingAddress = (address: string): BillingAddress => {
        const emptyAddress: BillingAddress = {
            city: '',
            country: '',
            emails: [],
            houseNumber: '',
            postalCode: '',
            street: '',
            showVatNumber: false,
            isCitizen: false,
            showEmail: true,
            fullName: ''
        };

        if (!address) {
            return emptyAddress;
        }

        const result = { ...emptyAddress };
        if (!address.includes(ADDRESS_SEPARATOR)) {
            result.street = address;
            return result;
        }

        const parsedAddress = parseAddressParts(address);
        const parsedStreet = parseStreetDetails(parsedAddress.street);

        result.street = parsedStreet.streetName;
        result.houseNumber = parsedStreet.houseNumber;
        result.boxNumber = parsedStreet.boxNumber;

        const cityDetails = parseCityDetails(parsedAddress.cityPart);
        result.postalCode = cityDetails.postalCode;
        result.city = cityDetails.city;

        return result;
    };

    const parseAddressParts = (address: string): { street: string; cityPart: string; } => {
        const [street, cityPart] = address.split(ADDRESS_SEPARATOR);
        return { street, cityPart };
    };

    const parseStreetDetails = (streetDetails: string): { streetName: string; houseNumber: string; boxNumber: string} => {
        if (!streetDetails.includes(' ')) {
            return { streetName: streetDetails, houseNumber: '', boxNumber: '' };
        }
        
        const parts = streetDetails.split(' ');
        const lastPart = parts[parts.length - 1];

        // Check if the last part is a number or contains a house number with box number
        if (isNumeric(lastPart) || lastPart.match(/^\d+[a-zA-Z]*$/)) {
            const streetName = parts.slice(0, -1).join(' ');
            if (isNumeric(lastPart)) {
                return { streetName, houseNumber: lastPart, boxNumber: '' };
            }
            const potentialHouseAndBox = parseHouseNumberAndBoxNumber(lastPart);
            return { streetName, houseNumber: potentialHouseAndBox.houseNumber, boxNumber: potentialHouseAndBox.boxNumber };
        }

        // If the last part is not a number, treat the whole string as the street name
        return { streetName: streetDetails, houseNumber: '', boxNumber: '' };
    };


    const parseCityDetails = (cityDetails: string): { city: string; postalCode: string } => {
        if (!cityDetails.includes(' ')) {
            return { city: cityDetails, postalCode: '' };
        }

        const [firstPart, ...restParts] = cityDetails.split(' ');
        // There are some cities with spaces in the name (ex: De Haan, De Pinte), se we need to check if the possible zipcode is a number.
        const isZipCode = isNumeric(firstPart);                         

        return isZipCode
            ? { postalCode: firstPart, city: restParts.join(' ') }
            : { postalCode: '', city: cityDetails };
    };

    const parseHouseNumberAndBoxNumber = (houseNumber: string): { houseNumber: string; boxNumber: string } => {
        const defaultResult = {
            houseNumber: '',
            boxNumber: ''
        };

        if (isEmpty(houseNumber)) {
            return defaultResult;
        }

        const parts = houseNumber.match(/[a-z]+|[^a-z]+/gi);
        if (!parts) {
            return defaultResult;
        }

        return {
            houseNumber: parts[0] || '',
            boxNumber: parts[1] || ''
        };
    }
    
    const isNumeric = (value: string): boolean => !isNaN(+value);

    const reset = () => { 
        streetData.value = [];  
        houseNumberData.value = [];
    };
    
    return {
        streetData: readonly(streetData),
        streetDataFetching: readonly(streetDataFetching),
        houseNumberData: readonly(houseNumberData),
        houseNumberDataFetching: readonly(houseNumberDataFetching),
        fetchStreetSuggestions,
        fetchHouseNumberSuggestions,
        parseBillingAddress,
        parseHouseNumberAndBoxNumber,
        fetchPostalCodes,
        reset
    };
});